'use client';
import React from 'react';
import { isObject } from '@benzinga/utils';
import { useQueryParams } from './useQueryParams';
import { QueryParams } from '../entities';

interface UseFiltersOptions {
  history?: boolean;
  hideQueryKeys?: string[];
}

const isQueryKeyHidden = (key: string, queryKeys?: string[]) => {
  return Array.isArray(queryKeys) && queryKeys.includes(key);
};

const getInitialFilters = (initialFilters: QueryParams, queryParams: QueryParams) => {
  const data = {};

  if (isObject(initialFilters)) {
    Object.keys(initialFilters).forEach(key => {
      data[key] = queryParams[key] !== undefined ? queryParams[key] : initialFilters[key];
    });
  }

  return data;
};

export const useFilters = <T extends unknown>(
  initialFilters: Record<any, any>,
  options?: UseFiltersOptions,
): [T | QueryParams, (data: T | QueryParams) => void] => {
  const [queryParams, updateQueryParams] = useQueryParams();
  const [filters, setFilters] = React.useState<T | QueryParams>(getInitialFilters(initialFilters, queryParams));

  const filterData = React.useCallback(
    (income: T | Record<string, any>) => {
      const data = {};

      if (isObject(income)) {
        Object.keys(income).forEach(key => {
          if (isQueryKeyHidden(key, options?.hideQueryKeys)) {
            return;
          }

          data[key] = income[key];
        });
      }

      return data;
    },
    [options?.hideQueryKeys],
  );

  const updateFilters = React.useCallback(
    (data: T | QueryParams): T | QueryParams => {
      setFilters(data);

      if (options?.history !== false) {
        if (options?.hideQueryKeys) {
          updateQueryParams(filterData(data), 'replace');
        } else {
          updateQueryParams(data as QueryParams, 'replace');
        }
      }

      return data;
    },
    [filterData, options?.hideQueryKeys, options?.history, updateQueryParams],
  );

  return [filters, updateFilters];
};
